<svelte:options customElement="dst-readinglist" />

<script lang="ts">
	import '@oddbird/popover-polyfill';
	import { afterUpdate, onDestroy, onMount } from 'svelte';
	import { cubicInOut } from 'svelte/easing';
	import { fade, scale } from 'svelte/transition';
	import { getPlatform } from '@derstandard/utils';
	import { shortcut } from '$lib/shortcut';
	import { trackUserAction } from '$lib/tracking';
	import { userIsLoggedIn } from '$lib/authentication';
	import languageStrings from '$i18n/de';
	import store from '$lib/store';

	let listenerAttached: boolean = false;
	let flyoutElement: HTMLElement;
	let isMacOS: boolean = false;
	let modifierKey: string;
	let toggleElement: HTMLElement;
	let tableIsEmpty: boolean = false;

	const performanceNavigation = window.performance.getEntriesByType('navigation')[0];

	$: entries = store('readinglist', isManualReload);
	$: containsVisited = $entries?.some((item) => item.visited);
	$: isLoggedIn = false;
	$: isManualReload = (performanceNavigation as PerformanceNavigationTiming).type === 'reload';

	// If not undefined, the current page is an article in the list.
	$: pageItem = $entries?.find((item) => new URL(item.url).pathname === document.location.pathname);

	let animate: boolean = false;
	let pastEntriesLength: number = $entries?.length;

	$: if ($entries.length === 0) {
		pastEntriesLength = 0;
		tableIsEmpty = true;
		flyoutElement?.hidePopover();
	}

	// Entry added, animate Fab.
	$: if ($entries.length > 0) {
		if ($entries.length > pastEntriesLength) {
			animate = false;
			setTimeout(() => (animate = true), 100);
		}

		pastEntriesLength = $entries.length;
	}

	// This message handler revalidates the login-state on state change.
	const onMessage = async (event: MessageEvent) => {
		if (event.data.eventName !== 'dst.auth.stateChanged' && event.data.eventName !== 'dst.user.loggedIn') {
			return;
		}

		if (event.data.eventName === 'dst.auth.stateChanged') {
			isLoggedIn = await userIsLoggedIn();
		}

		if (event.data.eventName === 'dst.user.loggedIn') {
			store('readinglist', isManualReload);
		}
	};

	onMount(async () => {
		window.addEventListener('message', onMessage);

		isLoggedIn = await userIsLoggedIn();
		isMacOS = getPlatform() === 'mac';
		modifierKey = isMacOS ? 'Control' : 'Alt';

		if (pageItem && !pageItem.visited) {
			postMessage({ action: 'visit', item: pageItem });
		}
	});

	afterUpdate(() => {
		// Needed since the flyoutElement is conditionally rendered and might still be undefined when component mounts.
		if (flyoutElement && !listenerAttached) {
			flyoutElement.addEventListener('click', handleOutsideClick);
			listenerAttached = true;
		}
	});

	onDestroy(() => {
		window.removeEventListener('message', onMessage);

		if(flyoutElement && listenerAttached) {
			flyoutElement?.removeEventListener('click', handleOutsideClick);
			listenerAttached = false;
		}
	});

	function handleOutsideClick(event: MouseEvent) {
		if (event.target === flyoutElement) {
			flyoutElement.hidePopover();
		}
	}

	/**
	 * handles the redirect to the next or previous article in the list
	 * the last article redirects to the first and vice versa
	 * @param {string} direction - 'previous' or 'next'
	 */
	const redirect = (direction: 'previous' | 'next') => {
		const index = $entries.indexOf(pageItem) + (direction === 'next' ? 1 : -1);
		document.location.href = `${$entries[(index + $entries.length) % $entries.length].url}?ref=readlater_next`;
	};
</script>

{#if $entries.length > 0 && isLoggedIn}
	<div
		id="fab"
		part="fab"
		class:fab-animate={animate}
		class={pageItem && $entries.length > 1 ? `fab-expanded` : `fab-compact`}
		in:scale={{ start: 0, opacity: 0, duration: tableIsEmpty ? 400 : 0, delay: tableIsEmpty ? 250 : 0, easing: cubicInOut }}
		out:fade={{ duration: 200, delay: 100, easing: cubicInOut }}
	>
		{#if pageItem && $entries.length > 1}
			<button
				on:click={() => redirect('previous')}
				aria-label={languageStrings.readinglist.fab.previous.ariaLabel(modifierKey)}
				title={languageStrings.readinglist.fab.previous.title(modifierKey)}
				type="button"
				part="button-previous"
				use:shortcut={{
					control: true,
					shift: true,
					code: 'ArrowLeft',
					callback: () => redirect('previous'),
				}}
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
					<path fill="currentcolor" d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
				</svg>
			</button>
		{/if}

		<button
			aria-label={languageStrings.readinglist.fab.toggle.ariaLabel(modifierKey)}
			class="toggle"
			id="readinglist-toggle-button"
			popovertarget="flyout"
			title={languageStrings.readinglist.fab.toggle.title(modifierKey)}
			type="button"
			part="button-toggle"
			on:click={() => trackUserAction('readlater_list_open')}
			bind:this={toggleElement}
			use:shortcut={{
				control: true,
				shift: true,
				code: 'KeyR',
				callback: () => {
					if (!flyoutElement.matches(':popover-open')) {
						flyoutElement.showPopover();
						trackUserAction('readlater_list_open_kb');
					} else {
						flyoutElement.hidePopover();
					}
					toggleElement.focus();
				},
			}}
		>
			{#if pageItem && $entries.length > 1}
				{`${$entries.indexOf(pageItem) + 1} von ${$entries.length}`}
			{:else}
				{$entries.length}
			{/if}
		</button>

		{#if pageItem && $entries.length > 1}
			<button
				on:click={() => redirect('next')}
				aria-label={languageStrings.readinglist.fab.next.ariaLabel(modifierKey)}
				title={languageStrings.readinglist.fab.next.title(modifierKey)}
				type="button"
				part="button-next"
				use:shortcut={{
					control: true,
					shift: true,
					code: 'ArrowRight',
					callback: () => redirect('next'),
				}}
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
					<path fill="currentcolor" d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
				</svg>
			</button>
		{/if}
	</div>

	<nav id="flyout" bind:this={flyoutElement} popover="auto" part="flyout" aria-label={languageStrings.readinglist.list.ariaLabel}>
		<ul>
			{#each $entries as entry (entry.url)}
				<li class:active={entry?.url === pageItem?.url} class:visited={entry?.visited}>
					<a href={entry ? `${entry.url}?ref=readlater_article` : null}>
						<span>{decodeURIComponent(entry.title)}</span>
					</a>
					<button
						on:click={() => postMessage({ action: 'deleteItem', item: entry })}
						aria-label={languageStrings.readinglist.list.deleteLabel}
						class="form--button form--button-small form--button-text"
						type="button"
					>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
							<path
								fill="currentcolor"
								d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
							/>
						</svg>
					</button>
				</li>
			{/each}
		</ul>
		{#if containsVisited}
			<button on:click={() => postMessage({ action: 'removeVisited' })} class="clear form--button form--button-weak form--button-small" type="button">
				{languageStrings.readinglist.list.deleteReadLabel}
			</button>
		{:else}
			<button on:click={() => postMessage({ action: 'clear' })} class="clear all-items form--button form--button-weak form--button-small" type="button">
				{languageStrings.readinglist.list.deleteAllLabel}
			</button>
		{/if}
	</nav>
{/if}

<style lang="scss">
	@use '@derstandard/styling-toolkit/src/variables' as var;
	@use '@derstandard/styling-toolkit/src/a11y/focusstyles';
	@use '@derstandard/styling-toolkit/src/utility/dropshadow';
	@use '@derstandard/styling-toolkit/src/components/form/button';

	:host {
		bottom: var(--readinglist-fab-bottom, 16px);
		left: var(--readinglist-fab-left, 16px);
		position: fixed;
		z-index: 700;

		@media (min-width: var.$breakpoint-6xl) {
			left: var(--readinglist-fab-left, calc(16px + 338px));
		}
	}

	#fab {
		@extend .drop-shadow-xl;
		align-items: center;
		background-color: var(--theme--surface-2dp, #fff);
		border: 1px solid var(--theme--surface-2dp-border);
		color: hsl(var(--theme--on-surface--hsl) / 100%);
		border-radius: 27.5px;
		display: flex;
		justify-content: center;
		line-height: 1;
		transform-origin: center;
		transition:
			transform 0.3s,
			color 0.15s,
			background-color 0.15s,
			border-color 0.15s;
		white-space: nowrap;

		svg {
			flex-shrink: 0;
			height: 24px;
			width: 24px;
		}

		button {
			align-items: center;
			background: none;
			border-radius: 20px;
			border: 0;
			color: inherit;
			cursor: pointer;
			display: flex;
			font-family: inherit;
			font-size: 15px;
			font-weight: 600;
			justify-content: center;
			min-height: 40px;
			min-width: 40px;
			padding: 0;
			transition: opacity 0.2s;
			border: 4px solid transparent;
		}

		// Fab's toggle button ( < 10 von 20> )
		button.toggle {
			border: none;
			padding-inline: 10px;
		}

		@media (hover: hover) {
			button {
				&:hover {
					background: hsl(var(--theme--on-base--hsl) / 5%) content-box;
				}
			}

			button.toggle {
				&:hover {
					background: hsl(var(--theme--on-base--hsl) / 5%) border-box;
				}
			}
		}

		// Fab has popover open
		&:has(+ :popover-open) {
			background-color: var(--theme--highlight, rebeccapurple) !important;
			border-color: var(--theme--highlight, rebeccapurple) !important;
			color: var(--theme--on-highlight, #fff) !important;

			button.toggle {
				opacity: 1 !important;
			}

			button:not(.toggle) {
				opacity: 0.5 !important;
			}
		}

		// expanded Fab ( < 10 von 20> )
		&.fab-expanded {
			button.toggle {
				padding-inline: 40px;
				margin-inline: -40px;
				z-index: -1;
			}
		}

		@media (hover: hover) {
			&.fab-expanded:hover {
				button {
					opacity: 0.5;

					&:hover {
						opacity: 1;
					}
				}
			}
		}

		// animate Fab, new Item added
		&.fab-animate {
			animation: 1.5s cubic-bezier(0.645, 0.045, 0.355, 1) forwards fabAddedAnimation;
			background-color: var(--theme--surface-2dp, #fff);
			border-color: var(--theme--surface-2dp-border);
			color: hsl(var(--theme--on-surface--hsl) / 100%);
		}
	}

	@keyframes fabAddedAnimation {
		0% {
			background-color: var(--theme--surface-2dp, #fff);
			border-color: var(--theme--surface-2dp-border);
			color: hsl(var(--theme--on-surface--hsl) / 100%);
		}
		10% {
			background-color: var(--theme--highlight, rebeccapurple);
			border-color: var(--theme--highlight, rebeccapurple);
			color: var(--theme--on-highlight, #fff);
		}
		70% {
			background-color: var(--theme--highlight, rebeccapurple);
			border-color: var(--theme--highlight, rebeccapurple);
			color: var(--theme--on-highlight, #fff);
		}
		100% {
			background-color: var(--theme--surface-2dp, #fff);
			border-color: var(--theme--surface-2dp-border);
			color: hsl(var(--theme--on-surface--hsl) / 100%);
		}
	}

	// NOTE: tmp for onboarding
	#fab:has(button:nth-child(3)) {
		--scaleto: scalex(1.03) scaley(1.1);
	}

	@keyframes -global-highlightAnimation {
		20% {
			transform: translatex(-50%) translatey(-50%) scale(1);
		}
		50% {
			transform: translatex(-50%) translatey(-50%) var(--scaleto, scale(1.1));
		}
		80% {
			transform: translatex(-50%) translatey(-50%) scale(1);
		}
	}

	// Readinglist Flyout
	nav {
		background-color: var(--theme--surface-2dp);
		border: 1px solid var(--theme--surface-2dp-border);
		box-shadow: 0 25px 25px rgba(0, 0, 0, 0.15);
		color: var(--theme--on-surface);
		border-radius: 10px;
		bottom: var(--readinglist-flyout-bottom, 70px);
		box-sizing: border-box;
		flex-direction: column;
		left: var(--readinglist-flyout-left, 20px);
		max-height: calc(100svh - 100px);
		max-width: 560px;
		overflow-y: auto;
		position: fixed;
		right: unset;
		top: unset;
		width: calc(100vw - 40px);

		@media (min-width: var.$breakpoint-6xl) {
			left: var(--readinglist-flyout-left, calc(20px + 338px));
		}

		&:popover-open {
			display: flex;
		}

		&::after {
  	  content: '';
  	  position: fixed;
  	  inset: 0;
  	  z-index: -1;
  	}
		&::backdrop {
			background-color: rgba(
				0,
				0,
				0,
				0.3
			); // NOTE: ::backdrop cant be styled with custom props, var(--theme--backdrop); see https://twitter.com/Una/status/1545415642450477056
			z-index: -1;
		}
	}

	// NOTE: additional css for @oddbird/popover-polyfill
	@supports not selector(:popover-open) {
		:global([popover].\:popover-open) {
			display: flex;
		}

		:global([popover]:not(.\:popover-open)) {
			display: none;
		}
	}

	ul {
		display: flex;
		flex-direction: column;
		margin: 0;
		padding: 5px 0 0;
	}

	li {
		padding: 0 5px;
		align-items: center;
		display: flex;
		max-width: 100%;
		border-bottom: 1px solid hsl(var(--theme--on-base--hsl) / 10%);

		&.visited {
			font-weight: 420;
			color: hsl(var(--theme--on-surface--hsl) / 50%);
		}

		&.active {
			color: var(--theme--highlight, rebeccapurple);
			font-weight: bold;
		}

		button:not(:hover):not(:focus-visible) svg {
			opacity: 0.7;
		}
	}

	a {
		color: inherit;
		padding: 13px;
		flex-grow: 1;
		text-decoration: none;
		border-radius: 6px;

		span {
			-webkit-box-orient: vertical;
			-webkit-hyphenate-limit-after: 4;
			-webkit-hyphenate-limit-before: 4;
			-webkit-hyphens: auto;
			-webkit-line-clamp: 2;
			display: -webkit-box;
			hyphenate-limit-chars: 10 4 4;
			hyphens: auto;
			overflow: hidden;

			@media (min-width: var.$breakpoint-sm) {
				-webkit-line-clamp: 1;
			}
		}
	}

	.clear {
		margin: 14px 10px 10px;

		&.all-items {
			background: var(--theme--negative);
			color: var(--theme--on-negative);
		}
	}
</style>
